import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import { CartItemGifts as CoreCartItemGifts } from '@eliadmin/core/src/components/cart/detail/cart-item-utils/cart-gifts';
import {
  getProductMinQuantity,
  getProductQuantityStep,
  normalizeProductQuantity,
  normalizeProductQuantityDown,
} from '@eliadmin/core/src/utils/product-utils';
import type { CartItemType } from '@eliadmin/core/src/api/cart/cart-types';
import { CartShipmentDateDialog } from '@eliadmin/core/src/components/cart/detail/shipment-types/cart-shipment-date-dialog';
import { AvailabilityLabel } from '@eliadmin/core/src/components/product/availability-label';
import { NotInStockDialog } from '@eliadmin/core/src/components/product/dialog/not-in-stock-dialog';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useCart } from '@eliadmin/core/src/hooks/cart/use-cart';
import { CartSubitemEnum, useCartActions } from '@eliadmin/core/src/hooks/cart/use-cart-actions';
import { useDebouncedFn } from '@eliadmin/core/src/hooks/use-debounce';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getDateString } from '@eliadmin/core/src/utils/misc-utils';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { parseProductCode } from '@eliadmin/core/src/utils/proform-utils';
import { buildConfiguratorHref } from '@eliadmin/core/src/utils/configurator-route-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutationState } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { useEffect, useId, useState } from 'react';
import {
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiEdit2,
  FiMinus,
  FiPlus,
  FiTrash2,
} from 'react-icons/fi';

export type CartItemProps = {
  item: CartItemType;
};

function getCartItemHref(item: CartItemType) {
  if (!item.is_custom) {
    return item.slug ?? '#';
  }

  const { conf_id, id_nomen } = parseProductCode(item.code);
  if (!conf_id || !id_nomen) {
    return item.slug ?? '#';
  }

  return buildConfiguratorHref(`/configurator/${id_nomen}`, {
    confId: conf_id,
    cartHash: item.hash,
    returnPath: ROUTES.CART(),
  });
}

export function CartItem({ item }: CartItemProps) {
  const { t } = useTranslations();
  const url = getCartItemHref(item);

  return (
    <div className="flex flex-col gap-4 rounded-lg border-0 md:border md:p-4">
      <div className={cn('flex flex-col gap-4 md:flex md:flex-row')}>
        <div className={cn('hidden w-full flex-col gap-1 md:flex')}>
          <div className="flex h-full min-h-[100px] w-full justify-between md:min-h-0">
            <div className="flex h-full min-w-[100px] items-start justify-center">
              <ItemImage item={item} className="h-[100px] w-[100px]" />
            </div>

            <div className="ml-3 line-clamp-2 flex min-h-[100px] w-full max-w-[500px] shrink-[2] flex-col justify-between gap-2 md:min-h-0 lg:shrink">
              <ItemInfo item={item} className="max-w-full" />
              <div>
                <Button size="xs" className="gap-2 rounded-md border border-border" asChild>
                  <Link href={url}>
                    <FiEdit2 />
                    {t('text1199')}
                  </Link>
                </Button>
              </div>
              {!item.is_custom && <Stock item={item} className="flex flex-1  justify-start" />}
              {(!!item.sub_items.services?.length || !!item.sub_items.gifts?.length) && (
                <div className="mt-8 hidden flex-col gap-4 md:flex">
                  {!!item.sub_items.services?.length && <CartServices item={item} />}
                  {!!item.sub_items.gifts?.length && <CartItemGifts item={item} />}
                </div>
              )}
            </div>

            <div className="hidden h-12 w-full grid-cols-12 justify-between md:grid md:gap-1">
              {/* <Stock item={item} className="hidden flex-1 md:flex" /> */}
              <Quantity item={item} className="col-span-6 justify-start md:justify-center" />
              <UnitPrice item={item} className="col-span-3 justify-end md:justify-center" />
              <TotalPrice item={item} className="col-span-3 justify-center" />
            </div>
            <DeleteOrder item={item} className="flex h-12 flex-1 justify-end" />
          </div>
          <div className="flex w-full justify-between md:hidden">
            <Quantity item={item} className="flex-1 justify-start md:justify-center" />
            <div className="mr-2 flex gap-4">
              <TotalPrice item={item} className="justify-center" />
              <UnitPrice item={item} className="justify-end md:justify-center" />
            </div>
          </div>
        </div>
        <div className="flex h-full w-full gap-4 md:hidden">
          <div className="flex h-full min-w-[56px] items-start justify-center">
            <ItemImage item={item} className="h-[56px] w-[56px]" />
          </div>

          <div className="line-clamp-2 flex w-full flex-col gap-2">
            <div className="flex justify-between gap-4">
              <div className="flex flex-col gap-2">
                <ItemInfo item={item} className="max-w-full" />
                <Button size="xs" className="gap-2 rounded-md border border-border" asChild>
                  <Link href={url}>
                    <FiEdit2 />
                    {t('text1199')}
                  </Link>
                </Button>
                <Stock item={item} className="flex justify-start" />
                <Quantity item={item} className="flex flex-1 justify-start" />
              </div>
              <DeleteOrder item={item} className="flex h-12 justify-end" />
            </div>
            {(!!item.sub_items.services?.length || !!item.sub_items.gifts?.length) && (
              <div className="flex flex-col gap-4 md:hidden">
                {!!item.sub_items.services?.length && <CartServices item={item} />}
                {!!item.sub_items.gifts?.length && <CartItemGifts item={item} />}
              </div>
            )}
            <div className="flex gap-4">
              <div className="flex flex-1 flex-col gap-1">
                <span className="text-xs text-muted-foreground">{t('text483')}</span>
                <UnitPrice item={item} className="justify-end md:justify-center" />
              </div>
              <div className="flex flex-1 flex-col gap-1">
                <span className="text-xs text-muted-foreground">{t('text136')}</span>
                <TotalPrice item={item} className="justify-center" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full md:pl-28">
        {!!item.delivery_by_date && <CartItemTimeDelivery item={item} />}
      </div>
      <Separator className="md:hidden" />
    </div>
  );
}

export type CartItemSegment = {
  item: CartItemType & {
    price_subtotal_without_vat?: number;
    price_without_vat?: number;
  };
  className?: string;
  label?: boolean;
};

export type CartServicesProps = {
  className?: string;
  item: CartItemType;
};

function CartServices(props: CartServicesProps) {
  const currency = useGlobalStore(s => s.generalLayout.currency);

  const { updateCartMutation, getUpdateCartBody } = useCartActions();
  const updateCartMutationState = useMutationState({
    filters: { mutationKey: ['updateCartMutation'] },
    select: mutation => mutation.state.status,
  });

  const cart = useCart();

  const isLoading = updateCartMutationState.some(state => state === 'pending') || cart.isFetching;

  if (!props.item.sub_items.services) return null;
  else {
    return (
      <div className="flex flex-col gap-4">
        {props.item.sub_items.services.map(group => {
          const selectedCount = group.values.filter(v => v.selected).length;

          return (
            <div key={group.id} className="flex flex-col gap-2">
              {/* Group header */}
              <span className="font-semibold">{group.name}</span>

              {/* Each value in this group */}
              {group.values.map(value => (
                <div
                  className={cn(
                    'flex flex-row items-center gap-2',
                    !value.selected && selectedCount >= group.max_selections && 'opacity-50',
                  )}
                  key={value.id}
                >
                  <Checkbox
                    id={`service-${group.id}-${value.id}`}
                    checked={value.selected}
                    // disable if global loading OR this is unselected AND we've hit the max for this group
                    disabled={
                      isLoading || (!value.selected && selectedCount >= group.max_selections)
                    }
                    onClick={() =>
                      updateCartMutation.mutate(
                        getUpdateCartBody(props.item, CartSubitemEnum.SERVICE, value.id),
                      )
                    }
                  />
                  <label htmlFor={`service-${group.id}-${value.id}`} className="font-semibold">
                    {value.name}{' '}
                    <span className="text-success-foreground">
                      +{value.price_with_vat}
                      {currency}
                    </span>
                  </label>
                  {isLoading && <Spinner />}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  }
}

function CartItemTimeDelivery(props: CartItemProps) {
  const { t } = useTranslations();

  const { updateCartMutation } = useCartActions();
  const openDialog = useDialogStore(s => s.open);
  const locale = useLocaleStore(s => s.currentLocale);

  const [note, setNote] = useState<string | null>(props.item.note);
  const [selectedDay, setSelectedDay] = useState<Date | undefined>(
    props.item.time_of_delivery ? new Date(props.item.time_of_delivery) : undefined,
  );

  const baseId = useId();
  const dateId = `${baseId}-date`;
  const noteId = `${baseId}-note`;

  const handlePickShipmentDateClick = () => {
    openDialog({
      id: CartShipmentDateDialog.id,
      content: (
        <CartShipmentDateDialog
          pickDate={date => {
            setSelectedDay(date);
            update(note, date);
          }}
          selectedDay={selectedDay}
        />
      ),
    });
  };

  const update = (note: string | null, date: Date | undefined) => {
    updateCartMutation.mutate({
      hash: props.item.hash,
      item: {
        sub_items: props.item.sub_items,
        product: props.item.product,
        variant: props.item.variant,
      },
      time_of_delivery: date ? getDateString(date) : undefined,
      note: note ?? undefined,
      qty: 1,
    });
  };

  return (
    <div className="flex w-full max-w-[750px] flex-col items-start gap-4 md:flex-row ">
      <div className="flex w-full flex-1 flex-col items-start gap-1">
        <Label htmlFor={dateId}>{t('text727')}</Label>
        <Input
          id={noteId}
          placeholder={t('text727')}
          value={note ?? ''}
          onChange={e => setNote(e.target.value)}
          onBlur={() => update(note, selectedDay)}
          className={{ wrapper: 'w-full' }}
        />
      </div>
      <div className="flex w-full flex-1 flex-col items-start gap-1">
        <Label htmlFor={dateId}>{t('text708')}</Label>
        <Input
          id={dateId}
          placeholder={t('text708')}
          className={{ wrapper: 'w-full cursor-pointer', main: 'cursor-pointer' }}
          readOnly
          value={selectedDay ? selectedDay.toLocaleDateString(locale) : ''}
          onClick={handlePickShipmentDateClick}
          after={
            <Button
              size="icon-sm"
              variant="ghost"
              className="hover:text-primary-background mr-2 hover:bg-primary-foreground"
              onClick={handlePickShipmentDateClick}
            >
              <FiCalendar />
            </Button>
          }
        />
      </div>
    </div>
  );
}
export type CartItemGiftsProps = {
  className?: string;
  item: CartItemType;
};

function CartItemGifts({ item, className }: CartItemGiftsProps) {
  return <CoreCartItemGifts item={item} className={className} />;
}

function DeleteOrder(props: CartItemSegment) {
  const locale = useLocaleStore(state => state.currentLocale);
  const { deleteCartMutation } = useCartActions();

  function handleDelete() {
    deleteCartMutation.mutate({
      hash: props.item.hash,
      analytics: {
        id: String(props.item.id),
        name: props.item.name,
        price: Number(props.item.price),
        currency: useGlobalStore.getState().generalLayout.currency,
        quantity: props.item.qty,
      },
    });
  }

  return (
    <div className={cn('col-start-12 flex items-center justify-center', props.className)}>
      <Button
        onClick={handleDelete}
        type="button"
        aria-label={`${locale?.startsWith('cs') ? 'Odstranit z košíku' : locale?.startsWith('sk') ? 'Odstrániť z košíka' : 'Remove from cart'}: ${props.item.name}`}
        isLoading={deleteCartMutation.isPending}
        variant="ghost"
        size="icon"
      >
        <FiTrash2 className="h-5 w-5 text-destructive" />
      </Button>
    </div>
  );
}

function Quantity({ item, className }: CartItemSegment) {
  const locale = useLocaleStore(state => state.currentLocale);
  const quantityLabel = locale?.startsWith('cs') ? 'Množství' : locale?.startsWith('sk') ? 'Množstvo' : 'Quantity';
  const { updateQuantityMutation } = useCartActions();
  const openModal = useDialogStore(s => s.open);

  const [internalValue, setInternalValue] = useState<string>(String(item.qty));
  const debouncedMutate = useDebouncedFn(updateQuantityMutation.mutate, 1000);
  const minQuantity = getProductMinQuantity(item);
  const step = getProductQuantityStep(item);

  const isSellable = item.availability.sellable;

  const openNotInStockDialog = () => {
    openModal({
      id: NotInStockDialog.id,
      content: <NotInStockDialog maxQuantity={item.stock} unit={item.unit} />,
    });
  };

  useEffect(() => {
    setInternalValue(String(item.qty));
  }, [item.qty]);

  const disabled = item.delivery_by_date;

  return (
    <div
      className={cn(
        'my-2 flex flex-row items-center justify-center gap-1 md:my-0',
        disabled && 'pointer-events-none cursor-not-allowed opacity-50',
        className,
      )}
    >
      <Button
        variant="outline"
        size="icon"
        className="h-8 w-8 rounded-md md:h-12 md:w-12 md:rounded-lg"
        disabled={item.qty <= minQuantity}
        type="button"
        aria-label={`${locale?.startsWith('cs') ? 'Snížit množství' : locale?.startsWith('sk') ? 'Znížiť množstvo' : 'Decrease quantity'}: ${item.name}`}
        onClick={() => {
          const internalNumValue = Number(internalValue);
          if (!isNaN(internalNumValue) && internalNumValue > minQuantity) {
            setInternalValue(String(normalizeProductQuantity(internalNumValue - step, item)));
            debouncedMutate({
              hash: item.hash,
              qty: normalizeProductQuantity(internalNumValue - step, item),
            });
          }

          if (internalNumValue <= minQuantity) {
            setInternalValue(String(minQuantity));
          }
        }}
      >
        <div>
          <FiChevronLeft size={20} className={'hidden md:flex'} />
          <FiMinus size={20} className={'flex md:hidden'} />
        </div>
      </Button>
      <Input
        className={{
          wrapper: 'h-8 w-16 rounded-md border md:h-12 md:w-14 md:rounded-lg',
          main: 'text-center',
        }}
        value={internalValue}
        aria-label={`${quantityLabel}: ${item.name}`}
        onChange={e => {
          const strValue = e.target.value;
          setInternalValue(strValue);

          if (!strValue) return;

          const numValue = Number(strValue);
          if (isNaN(numValue)) return;

          if (numValue >= minQuantity && (numValue <= item.stock || isSellable)) {
            debouncedMutate({ hash: item.hash, qty: normalizeProductQuantity(numValue, item) });
          }

          // if (numValue > item.stock) {
          //   openNotInStockDialog();
          //   setInternalValue(String(normalizeProductQuantityDown(item.stock, item)));
          //   return;
          // }

          // if (numValue < minQuantity) {
          //   setInternalValue(String(minQuantity));
          //   return;
          // }
        }}
        onBlur={() => {
          const numValue = Number(internalValue);
          if (isNaN(numValue)) {
            setInternalValue(String(item.qty));
          }

          if (numValue < minQuantity) {
            setInternalValue(String(minQuantity));
          }

          if (numValue > item.stock && !isSellable) {
            openNotInStockDialog();
            setInternalValue(String(normalizeProductQuantityDown(item.stock, item)));
          }
        }}
      />
      <Button
        variant="outline"
        className="h-8 w-8 rounded-md md:h-12 md:w-12 md:rounded-lg"
        size="icon"
        // disabled={item.qty >= item.stock}
        type="button"
        aria-label={`${locale?.startsWith('cs') ? 'Zvýšit množství' : locale?.startsWith('sk') ? 'Zvýšiť množstvo' : 'Increase quantity'}: ${item.name}`}
        onClick={() => {
          const internalNumValue = Number(internalValue);

          if (internalNumValue >= item.stock && !isSellable) {
            return openNotInStockDialog();
          }

          if (!isNaN(internalNumValue) && (internalNumValue < item.stock || isSellable)) {
            setInternalValue(String(normalizeProductQuantity(internalNumValue + step, item)));
            debouncedMutate({
              hash: item.hash,
              qty: normalizeProductQuantity(internalNumValue + step, item),
            });
          }
        }}
      >
        <div>
          <FiChevronRight size={20} className={'hidden md:flex'} />
          <FiPlus size={20} className={'flex md:hidden'} />
        </div>
      </Button>
    </div>
  );
}

function UnitPrice({ item, label, className }: CartItemSegment) {
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const { t } = useTranslations();

  return (
    <div
      className={cn(
        'flex flex-col items-start justify-center font-bold md:items-center',
        className,
      )}
    >
      {label && <span className="text-sm font-semibold text-muted-foreground">{t('text663')}</span>}
      {formatPrice(item.price_without_vat, currency, currencySettings)}/{item.unit}
    </div>
  );
}
function TotalPrice({ item, label, className }: CartItemSegment) {
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const { t } = useTranslations();

  return (
    <div
      className={cn(
        'flex flex-col items-start justify-center font-bold md:items-center',
        className,
      )}
    >
      {label && <span className="text-sm font-semibold text-muted-foreground">{t('text663')}</span>}
      {formatPrice(item.price_subtotal_without_vat, currency, currencySettings)}
      {/* {formatPrice(372.24, currency, currencySettings)} */}
    </div>
  );
}

function Stock({ className, item }: CartItemSegment) {
  return (
    <div className={cn('flex items-center justify-center', className)}>
      <AvailabilityLabel
        isAvailable={item.stock > 0 || item.availability.sellable}
        customName={item.availability.name}
      />
    </div>
  );
}

function ItemInfo({ item, className }: CartItemSegment) {
  const { t } = useTranslations();
  const templateSettings = useGlobalStore(s => s.templateSettings);

  const url = getCartItemHref(item);

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="flex flex-row items-center gap-2">
        <Link href={url} className="" prefetch={false}>
          <h6 className="text-base font-bold">{item.name}</h6>
        </Link>
      </div>
      {!!item.sub_items.packages?.length && (
        <div className="text-sm text-muted-foreground">
          {item.sub_items.packages
            .filter(p => p.selected)
            .map(p => p.name)
            .join(', ')}
        </div>
      )}
      {!!templateSettings?.cart?.show_cart_item_attributes?.enabled &&
        item.attributes?.map((attr, i) => (
          <span key={i} className="text-sm text-muted-foreground">
            {attr.attribute_name}: {attr.name}
          </span>
        ))}
      <small className="text-muted-foreground">
        {t('text19')}: {item.code}
      </small>
      {/* <span className="attribute">Product attribute</span> */}
    </div>
  );
}

function ItemImage({ item, className }: CartItemSegment) {
  return (
    <Image
      alt={resolveImageAlt(item?.image, item?.name)}
      src={item.image.url}
      width="0"
      height="0"
      sizes="100vw"
      className={cn('h-full w-full rounded-lg object-contain', className)}
    />
  );
}
